import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppSettings } from 'src/app/app-settings';
import { ProductService } from 'src/app/services/product.service';
import { PanelTitleService } from 'src/app/services/panel-title.service';
import { InfoCardComponent } from '../../../shared/info-card/info-card.component';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss']
})
export class ProductFormComponent implements OnInit, OnDestroy {

  form: FormGroup;
  isEdit: boolean = false;
  productId: number | null = null;
  loaded: boolean = false;
  saving: boolean = false;

  business: any;

  // Imagen: el fichero elegido viaja en base64 dentro de `file`, y `image`
  // mantiene el nombre del fichero ya almacenado (o null si se ha quitado).
  // Mismo convenio que el resto de imágenes (events_images, spaces…): la barra
  // va aquí porque MEDIA_URI no la lleva en pre/prod.
  urlImages: string = AppSettings.MEDIA_URI + '/products_images/';
  allowedImageTypes: string[] = ['image/jpeg', 'image/png', 'image/webp'];
  maxImageSize: number = 2 * 1024 * 1024;
  storedImage: string | null = null;
  imageBase64: string | null = null;

  // Solo informativo: la asignación producto↔(temporada, categoría, nivel de
  // butaca) se hace desde la ficha de la temporada. Aquí no se envía `seasons`
  // —el backend lo ignora— y las asignaciones existentes quedan intactas.
  linkedSeasonNames: string[] = [];

  private sub: Subscription;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private panelTitleService: PanelTitleService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.business = JSON.parse(localStorage.getItem('business') || '{}');

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      price: [null, [Validators.required, Validators.min(0)]],
      // Canal de venta. Por defecto web + panel: lo excepcional es la
      // invitación o el descuento que solo concede el administrador.
      admin_only: [false],
    });

    this.sub = this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEdit = true;
        this.productId = +params['id'];
        this.loadProduct(this.productId);
      } else {
        this.loaded = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.panelTitleService.setSubtitle(null);
    if (this.sub) this.sub.unsubscribe();
  }

  loadProduct(id: number) {
    this.productService.getProductById(this.business.id, id).subscribe({
      next: (res) => {
        const p = res.data;
        this.panelTitleService.setSubtitle(p.name);
        this.form.patchValue({
          name: p.name,
          price: p.price !== null && p.price !== undefined ? +p.price : null,
          admin_only: !!p.admin_only,
        });
        this.storedImage = p.image || null;

        // Una misma temporada puede aparecer varias veces (una asignación por
        // categoría y nivel de butaca): aquí solo interesa el nombre, una vez.
        this.linkedSeasonNames = [...new Set<string>((p.seasons || []).map((s: any) => s.name))];

        this.loaded = true;
      },
      error: () => {
        this.openInfoCard('Error al cargar el producto');
        this.router.navigate(['/panel-control/sports-passes/products']);
      }
    });
  }

  onImageSelected(event: any) {
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    if (!this.allowedImageTypes.includes(file.type)) {
      this.openInfoCard('La imagen debe ser JPG, PNG o WEBP');
      event.target.value = '';
      return;
    }

    if (file.size > this.maxImageSize) {
      this.openInfoCard('La imagen no puede ocupar <br> más de 2 MB.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.imageBase64 = reader.result as string;
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  removeImage() {
    this.imageBase64 = null;
    this.storedImage = null;
  }

  get imagePreview(): string | null {
    if (this.imageBase64) return this.imageBase64;
    if (!this.storedImage) return null;
    return this.storedImage.startsWith('http') ? this.storedImage : this.urlImages + this.storedImage;
  }

  get canSave(): boolean {
    return this.form.valid && !this.saving;
  }

  save() {
    if (!this.canSave) return;
    this.saving = true;

    const payload: any = {
      name: this.form.value.name,
      price: +this.form.value.price,
      admin_only: !!this.form.value.admin_only,
      image: this.storedImage,
    };

    if (this.imageBase64) {
      payload.file = this.imageBase64;
    }

    if (this.isEdit && this.productId) {
      this.productService.updateProduct(this.business.id, this.productId, payload).subscribe({
        next: () => {
          this.saving = false;
          this.openInfoCard('Producto actualizado correctamente');
          this.router.navigate(['/panel-control/sports-passes/products']);
        },
        error: (err) => {
          this.saving = false;
          this.openInfoCard(this.extractErrorMessage(err, 'Error al actualizar el producto'));
        }
      });
    } else {
      this.productService.createProduct(this.business.id, payload).subscribe({
        next: () => {
          this.saving = false;
          this.openInfoCard('Producto creado correctamente');
          this.router.navigate(['/panel-control/sports-passes/products']);
        },
        error: (err) => {
          this.saving = false;
          this.openInfoCard(this.extractErrorMessage(err, 'Error al crear el producto'));
        }
      });
    }
  }

  cancel() {
    this.router.navigate(['/panel-control/sports-passes/products']);
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 422 && err?.error?.errors && typeof err.error.errors === 'object' && !Array.isArray(err.error.errors)) {
      const first: any = Object.values(err.error.errors)[0];
      if (Array.isArray(first) && first.length) {
        return first[0];
      }
    }
    return err?.error?.message || fallback;
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }
}
